import {
  EMBLEMS,
  generateBadge,
  LAYOUTS,
  PATTERNS,
  renderBadgeSvg,
  rerollBadge,
  SHAPES,
  svgDataUri,
  type BadgeKind,
  type BadgeSpec,
} from '@fp/badges';
import { useMemo } from 'react';
import { ColorField, Field } from './ui';

/** A generated badge. Never a real crest: drawn from its spec. */
export function Badge({ spec, size = 28, title }: { spec: BadgeSpec | null | undefined; size?: number; title?: string }) {
  const src = useMemo(() => (spec ? svgDataUri(renderBadgeSvg(spec)) : null), [spec]);
  if (!src) return <span style={{ width: size, height: size * 1.2, display: 'inline-block' }} aria-hidden="true" />;
  return <img className="badge-img" src={src} width={size} height={Math.round(size * 1.2)} alt={title ?? ''} />;
}

const label = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Full badge editor: colours, letters and design. "Shuffle design" re-rolls
 * shape, pattern, emblem and layout from a new seed; the selects pick them by hand.
 */
export function BadgeEditor({ value, onChange, name }: { value: BadgeSpec; onChange: (b: BadgeSpec) => void; name: string }) {
  const set = (patch: Partial<BadgeSpec>) => onChange({ ...value, ...patch });
  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 20 }}>
      <div className="stack" style={{ alignItems: 'center', width: 132 }}>
        <Badge spec={value} size={112} title={`${name} badge preview`} />
        <button type="button" className="btn btn-sm" onClick={() => onChange(rerollBadge(value, Math.random().toString(36).slice(2, 8)))}>
          Shuffle design
        </button>
      </div>
      <div className="form-grid grow" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
        <ColorField label="Main colour" value={value.primary} onChange={(primary) => set({ primary })} />
        <ColorField label="Second colour" value={value.secondary} onChange={(secondary) => set({ secondary })} />
        <ColorField label="Emblem colour" value={value.accent} onChange={(accent) => set({ accent })} />
        <Field label="Letters" hint="1 to 3 capitals or digits">
          <input
            className="input"
            value={value.text}
            maxLength={3}
            onChange={(e) => set({ text: e.target.value.toUpperCase().replace(/[^\p{Lu}\p{N}]/gu, '') || value.text })}
          />
        </Field>
        <Field label="Shape">
          <select className="select" value={value.shape} onChange={(e) => set({ shape: e.target.value as BadgeSpec['shape'] })}>
            {SHAPES.map((s) => (
              <option key={s} value={s}>
                {label(s)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Pattern">
          <select className="select" value={value.pattern} onChange={(e) => set({ pattern: e.target.value as BadgeSpec['pattern'] })}>
            {PATTERNS.map((s) => (
              <option key={s} value={s}>
                {label(s)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Emblem">
          <select className="select" value={value.emblem} onChange={(e) => set({ emblem: e.target.value as BadgeSpec['emblem'] })}>
            {EMBLEMS.map((s) => (
              <option key={s} value={s}>
                {s === 'stars3' ? 'Three stars' : label(s)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Layout">
          <select className="select" value={value.layout} onChange={(e) => set({ layout: e.target.value as BadgeSpec['layout'] })}>
            {LAYOUTS.map((s) => (
              <option key={s} value={s}>
                {s === 'stack' ? 'Emblem and letters' : s === 'emblem' ? 'Emblem only' : 'Letters only'}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

/** A starting badge for a new record, regenerated as the name changes until the admin edits it. */
export function useDefaultBadge(kind: BadgeKind, name: string, shortName?: string | null): BadgeSpec {
  return useMemo(() => generateBadge({ kind, name: name.trim() || '?', shortName }), [kind, name, shortName]);
}
